import { useEffect, useState } from "react";
import { Box, Button, FormControl, IconButton, Spinner, Text, TextInput } from "@primer/react";
import { Dialog } from "@primer/react/experimental";
import {
  ArrowRightIcon,
  CheckIcon,
  CreditCardIcon,
  TagIcon,
  XIcon,
} from "@primer/octicons-react";
import { useAuth } from "../contexts/AuthContext";
import { API_ENDPOINTS } from "../config/api";

interface WalletTopupModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onSuccess?: () => void;
}

interface TopupAmount {
  label: string;
  value: number;
  tokens: number;
}

const TOPUP_AMOUNTS: TopupAmount[] = [
  { label: "$10", value: 10, tokens: 1000 },
  { label: "$25", value: 25, tokens: 2500 },
  { label: "$50", value: 50, tokens: 5000 },
  { label: "$100", value: 100, tokens: 10000 },
  { label: "Custom", value: -1, tokens: -1 },
];

type PaymentMethod = "creditcard" | "cashtoken";

export function WalletTopupModal({
  isOpen,
  onDismiss,
  onSuccess,
}: WalletTopupModalProps) {
  const { user } = useAuth();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("creditcard");
  const [selectedAmount, setSelectedAmount] = useState<TopupAmount>(TOPUP_AMOUNTS[0]);
  const [customAmount, setCustomAmount] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [cashToken, setCashToken] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    document.body.classList.add("app-modal-open");
    return () => document.body.classList.remove("app-modal-open");
  }, [isOpen]);

  const currentAmount = selectedAmount.value === -1 ? parseFloat(customAmount) || 0 : selectedAmount.value;
  const currentTokens = Math.floor(currentAmount * 100);
  const isValidAmount = currentAmount >= 10;

  const selectPaymentMethod = (method: PaymentMethod) => {
    setPaymentMethod(method);
    setError(null);
    setShowConfirmation(false);
  };

  const handleCreditCardPayment = async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(API_ENDPOINTS.CREATE_CHECKOUT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await user.getIdToken()}`,
        },
        body: JSON.stringify({ amount: currentAmount }),
      });

      if (!response.ok) throw new Error("Failed to create checkout session");
      const { checkout_url } = await response.json();
      window.location.href = checkout_url;
    } catch (err) {
      console.error("Error creating checkout:", err);
      setError("Failed to initialize payment. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCashTokenRedemption = async () => {
    if (!user || !cashToken.trim()) {
      setError("Please enter a valid token code");
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(API_ENDPOINTS.REDEEM_TOKEN, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await user.getIdToken()}`,
        },
        body: JSON.stringify({ token_code: cashToken.trim() }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || "Failed to redeem token");
      }

      const { tokens_added, new_balance } = await response.json();
      onDismiss();
      onSuccess?.();
      alert(`Successfully redeemed ${tokens_added} tokens! New wallet balance: ${new_balance} tokens`);
    } catch (err) {
      console.error("Error redeeming token:", err);
      setError(err instanceof Error ? err.message : "Failed to redeem token. Please check your token code and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    if (paymentMethod === "cashtoken") {
      void handleCashTokenRedemption();
      return;
    }
    if (!isValidAmount) {
      setError("Please enter a valid amount (minimum $10.00)");
      return;
    }
    if (showConfirmation) {
      void handleCreditCardPayment();
    } else {
      setError(null);
      setShowConfirmation(true);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog
      onClose={onDismiss}
      width="large"
      className="wallet-topup-dialog"
      sx={{ maxWidth: "560px !important" }}
      renderHeader={() => (
        <Box className="wallet-topup-header">
          <Box className="wallet-topup-header-copy">
            <Box className="wallet-topup-header-icon"><CreditCardIcon size={16} /></Box>
            <Box>
              <Text as="h2">Add funds</Text>
              <Text>Purchase tokens or redeem a cash token.</Text>
            </Box>
          </Box>
          <IconButton icon={XIcon} aria-label="Close" onClick={onDismiss} variant="invisible" />
        </Box>
      )}
    >
      <Box className="wallet-topup-content">
        {showConfirmation ? (
          <Box className="wallet-confirmation">
            <Box className="wallet-confirmation-mark"><CheckIcon size={18} /></Box>
            <Text as="h3">Confirm checkout</Text>
            <Text className="wallet-confirmation-copy">You will be redirected to our secure payment provider to complete this purchase.</Text>
            <Box className="wallet-checkout-summary">
              <Box><span>Tokens</span><strong>{currentTokens.toLocaleString()}</strong></Box>
              <Box><span>Total</span><strong>${currentAmount.toFixed(2)}</strong></Box>
            </Box>
            {error && <Box className="wallet-form-error">{error}</Box>}
            <Box className="wallet-topup-footer">
              <Button variant="default" onClick={() => { setShowConfirmation(false); setError(null); }} disabled={isLoading}>Back</Button>
              <Button variant="primary" className="app-primary-button" onClick={handleSubmit} disabled={isLoading} leadingVisual={isLoading ? Spinner : ArrowRightIcon}>
                {isLoading ? "Opening checkout…" : "Continue to payment"}
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Box className="wallet-method-switch" role="tablist" aria-label="Payment method">
              <button type="button" role="tab" aria-selected={paymentMethod === "creditcard"} className={paymentMethod === "creditcard" ? "is-active" : ""} onClick={() => selectPaymentMethod("creditcard")}>
                <CreditCardIcon size={15} /> Card
              </button>
              <button type="button" role="tab" aria-selected={paymentMethod === "cashtoken"} className={paymentMethod === "cashtoken" ? "is-active" : ""} onClick={() => selectPaymentMethod("cashtoken")}>
                <TagIcon size={15} /> Cash token
              </button>
            </Box>

            {paymentMethod === "creditcard" ? (
              <>
                <Box className="wallet-section-heading"><Text as="h3">Choose an amount</Text><Text>100 tokens per $1</Text></Box>
                <Box className="wallet-amount-grid">
                  {TOPUP_AMOUNTS.map((amount) => {
                    const active = selectedAmount.value === amount.value;
                    return (
                      <button
                        type="button"
                        key={amount.value}
                        className={active ? "wallet-amount-choice is-active" : "wallet-amount-choice"}
                        onClick={() => { setSelectedAmount(amount); if (amount.value !== -1) setCustomAmount(""); setError(null); }}
                      >
                        <strong>{amount.label}</strong>
                        <span>{amount.value === -1 ? "Enter an amount" : `${amount.tokens.toLocaleString()} tokens`}</span>
                      </button>
                    );
                  })}
                </Box>
                {selectedAmount.value === -1 && (
                  <FormControl className="wallet-custom-amount">
                    <FormControl.Label>Custom amount</FormControl.Label>
                    <TextInput type="number" min="10" step="0.01" value={customAmount} onChange={(event) => setCustomAmount(event.target.value)} placeholder="Minimum $10.00" />
                    <FormControl.Caption>Minimum amount is $10.00.</FormControl.Caption>
                  </FormControl>
                )}
                <Box className="wallet-purchase-summary">
                  <Text>You receive</Text>
                  <strong>{currentTokens.toLocaleString()} tokens</strong>
                  <span>${currentAmount.toFixed(2)}</span>
                </Box>
              </>
            ) : (
              <Box className="wallet-token-entry">
                <Box className="wallet-section-heading"><Text as="h3">Redeem a cash token</Text><Text>Enter the code you received.</Text></Box>
                <FormControl>
                  <FormControl.Label>Cash token code</FormControl.Label>
                  <TextInput value={cashToken} onChange={(event) => setCashToken(event.target.value)} placeholder="Paste your token code" autoComplete="off" />
                </FormControl>
              </Box>
            )}

            {error && <Box className="wallet-form-error">{error}</Box>}
            <Box className="wallet-topup-footer">
              <Text>{paymentMethod === "creditcard" ? "Secure checkout by Polar" : "Tokens are added immediately after verification."}</Text>
              <Button variant="primary" className="app-primary-button" onClick={handleSubmit} disabled={isLoading} leadingVisual={isLoading ? Spinner : paymentMethod === "creditcard" ? ArrowRightIcon : CheckIcon}>
                {isLoading ? "Processing…" : paymentMethod === "creditcard" ? "Review purchase" : "Redeem token"}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
}
